import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

import type { Job, JobEventLite } from '@/gen/cortado/v1/jobs_pb'
import type { InvoiceLite } from '@/gen/cortado/v1/invoices_pb'
import { TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency } from '@/lib/currency-utils'
import { formatShortDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useTasksList } from '@/stores/tasks.store'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  jobBookedEvents,
  jobBookingTotals,
  jobLastBookedEvent,
  jobOldestUnpaidInvoice,
} from '@/components/pipeline/job-detail-view'

// Same statuses as the Tasks card, so both read one cached ListTasks.
const OPEN_TASK_STATUSES = [TaskStatusEnum.TASK_STATUS_NOT_STARTED, TaskStatusEnum.TASK_STATUS_IN_PROGRESS]

interface JobWrapUpProps {
  job: Job
  events: JobEventLite[]
  /** The job's invoices, fetched by the page (the Booking card reads them too). */
  invoices: InvoiceLite[]
  isInvoicesLoading: boolean
}

function money(cents: bigint): string {
  return `$${formatCurrency(Number(cents))}`
}

/** One recap figure: a label, a large value and an optional muted sub-line. */
function RecapStat({
  label,
  value,
  sub,
  isLoading = false,
  isOpen = false,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  isLoading?: boolean
  /** Still left to close: the sub-line reads as a warning. */
  isOpen?: boolean
}) {
  return (
    <div role="group" aria-label={label} className="min-w-0 rounded-lg border px-3 py-2.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      {isLoading ? (
        <Skeleton className="mt-1.5 h-6 w-20" />
      ) : (
        <>
          <div className="font-financial text-xl font-semibold">{value}</div>
          {sub && (
            <div className={cn('text-xs', isOpen ? 'text-destructive' : 'text-muted-foreground')}>{sub}</div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Wrap-up card on the pipeline job page, shown only once the job is in the
 * Complete phase: a recap of the booking (money collected, events, guests,
 * open tasks) and whether anything is still left to close it out. Booking the
 * same customer again is the job header's primary action.
 */
export function JobWrapUp({ job, events, invoices, isInvoicesLoading }: JobWrapUpProps) {
  const { totalCount: openTaskCount, hasLoaded: hasTasksLoaded } = useTasksList({
    jobId: job.id,
    statuses: OPEN_TASK_STATUSES,
  })

  const bookedEvents = jobBookedEvents(events)
  const lastEvent = jobLastBookedEvent(events)
  const totals = jobBookingTotals(invoices)
  const unpaidInvoice = jobOldestUnpaidInvoice(invoices)

  const isInvoiced = totals.invoiceCount > 0
  const isPaid = isInvoiced && totals.dueCents <= 0n
  const hasNoOpenTasks = openTaskCount === 0
  const openItems = [isPaid || !isInvoiced, hasNoOpenTasks].filter((done) => !done).length
  const isSettled = !isInvoicesLoading && hasTasksLoaded

  const guestCounts = bookedEvents.map((event) => event.eventGuests ?? 0)
  const guestTotal = guestCounts.reduce((sum, guests) => sum + guests, 0)

  return (
    <Card className="gap-3 pt-4" aria-label="Wrap-up">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Wrap-up</CardTitle>
          {isSettled && (
            <Badge variant={openItems === 0 ? 'secondary' : 'outline'}>
              {openItems === 0 ? 'Closed out' : `${openItems} left to close`}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <RecapStat
            label="Collected"
            isLoading={isInvoicesLoading}
            isOpen={isInvoiced && !isPaid}
            value={money(totals.paidCents)}
            sub={
              !isInvoiced ? (
                'Nothing invoiced'
              ) : isPaid ? (
                `of ${money(totals.invoicedCents)} invoiced`
              ) : (
                <>
                  {money(totals.dueCents)} still due
                  {unpaidInvoice && (
                    <>
                      {' · '}
                      <Link
                        to="/pipeline/$id/invoices/$invoiceId"
                        params={{ id: job.id, invoiceId: unpaidInvoice.id }}
                        className="underline-offset-2 hover:underline"
                      >
                        Invoice #{unpaidInvoice.invoiceNumber} →
                      </Link>
                    </>
                  )}
                </>
              )
            }
          />
          <RecapStat
            label="Events"
            value={bookedEvents.length}
            sub={
              lastEvent?.eventDate
                ? `${bookedEvents.length > 1 ? 'Last on' : 'On'} ${formatShortDate(lastEvent.eventDate, lastEvent.eventTimezone || undefined)}`
                : undefined
            }
          />
          <RecapStat
            label="Guests"
            value={guestTotal > 0 ? guestTotal.toLocaleString() : '—'}
            sub={guestTotal > 0 && guestCounts.length > 1 ? guestCounts.join(' + ') : undefined}
          />
          <RecapStat
            label="Open tasks"
            isLoading={!hasTasksLoaded}
            isOpen={!hasNoOpenTasks}
            value={openTaskCount}
            sub={hasNoOpenTasks ? 'All done' : 'On the Tasks card'}
          />
        </div>
      </CardContent>
    </Card>
  )
}
